import { Phone, Printer, MapPin, Clock, Mail, Star } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { COMPANY } from "@/lib/constants";

export default function Contact() {
  useSEO({
    title: "Contact Esprit Home Healthcare | (703) 998-7400",
    description: "Contact Esprit Home Healthcare in Annandale, VA. Call (703) 998-7400 or fill out our form to start home care across Northern Virginia.",
    canonical: "/contact",
  });

  return (
    <div>
      <PageHeader
        overline="Contact Us"
        title="We're Here To Help"
        subtitle="Fill out the form and a member of our team will contact you to come up with the right care-giving plan for you & your family."
      />

      <section className="container-page py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <div className="space-y-5">
            <a href={COMPANY.phoneHref} className="flex items-center gap-4 bg-primary text-primary-foreground rounded-2xl p-6 hover:bg-primary/90 transition-colors">
              <span className="grid place-items-center w-14 h-14 rounded-xl bg-primary-foreground/15"><Phone className="w-7 h-7" /></span>
              <span>
                <span className="block text-sm text-primary-foreground/80">Office Main / After Hours</span>
                <span className="block font-heading text-3xl">{COMPANY.phone}</span>
              </span>
            </a>

            <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
              <div className="flex gap-4">
                <MapPin className="w-6 h-6 text-secondary shrink-0 mt-1" />
                <div><p className="font-semibold text-lg">Address</p><p className="text-muted-foreground">{COMPANY.address1}<br />{COMPANY.address2}</p></div>
              </div>
              <div className="flex gap-4">
                <Printer className="w-6 h-6 text-secondary shrink-0 mt-1" />
                <div><p className="font-semibold text-lg">Fax</p><p className="text-muted-foreground">{COMPANY.fax}</p></div>
              </div>
              <div className="flex gap-4">
                <Mail className="w-6 h-6 text-secondary shrink-0 mt-1" />
                <div><p className="font-semibold text-lg">Email</p><p className="text-muted-foreground">{COMPANY.email}</p></div>
              </div>
              <div className="flex gap-4">
                <Clock className="w-6 h-6 text-secondary shrink-0 mt-1" />
                <div className="w-full">
                  <p className="font-semibold text-lg mb-1">Hours</p>
                  <ul className="text-muted-foreground text-sm space-y-1">
                    {COMPANY.hours.map((h) => (
                      <li key={h.day} className="flex justify-between max-w-xs"><span className="font-medium">{h.day}</span><span>{h.time}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a href={COMPANY.directions} target="_blank" rel="noreferrer" data-testid="directions-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-5 font-semibold transition-colors">
                <MapPin className="w-5 h-5" /> Get Directions
              </a>
              <a href={COMPANY.gbp} target="_blank" rel="noreferrer" data-testid="gbp-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-12 px-5 font-semibold transition-colors">
                <Star className="w-5 h-5" /> View & Review on Google
              </a>
            </div>

            <div className="rounded-2xl overflow-hidden border border-border">
              <iframe title="Office location map" width="100%" height="260" style={{ border: 0 }} loading="lazy"
                src="https://www.google.com/maps?q=4209+Evergreen+Lane+Annandale+VA+22003&z=15&output=embed" />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-[hsl(var(--muted))] rounded-[2rem] border border-border p-6 sm:p-8">
            <h2 className="font-heading text-3xl text-primary mb-1">Request a Free Consultation</h2>
            <p className="text-muted-foreground mb-6">We'll respond promptly during business hours.</p>
            <LeadForm source="contact-page" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
